// Campo extra do tipo data: a tela mostra no padrão brasileiro (dd/MM/yyyy,
// MM/yyyy ou yyyy) e o valor salvo na API é ISO (yyyy-MM-dd, yyyy-MM ou yyyy).

export type CustomFieldDateType = 'full_date' | 'month_year' | 'year_only'

// Mesmo intervalo padrão do DatePicker do MUI (minDate/maxDate).
const MIN_YEAR = 1900
const MAX_YEAR = 2099

const MAX_DIGITS: Record<CustomFieldDateType, number> = {
  full_date: 8,
  month_year: 6,
  year_only: 4
}

const ISO_PATTERN: Record<CustomFieldDateType, RegExp> = {
  full_date: /^(\d{4})-(\d{2})-(\d{2})$/,
  month_year: /^(\d{4})-(\d{2})$/,
  year_only: /^(\d{4})$/
}

export const normalizeDateType = (dateType?: string): CustomFieldDateType =>
  dateType === 'month_year' || dateType === 'year_only' ? dateType : 'full_date'

const isValidParts = (year: number, month = 1, day = 1) => {
  if (year < MIN_YEAR || year > MAX_YEAR) return false
  const date = new Date(year, month - 1, day)
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  )
}

// Quantos dígitos já foram digitados quando começa cada parte que pode vir
// sem zero à esquerda (dia e mês).
const PAD_BOUNDARIES: Record<CustomFieldDateType, number[]> = {
  full_date: [0, 2],
  month_year: [0],
  year_only: []
}

const PASTED_ISO = /^\s*(\d{4})-(\d{1,2})(?:-(\d{1,2}))?\s*$/

const pad = (value: string) => value.padStart(2, '0')

// Data colada em ISO ("2018-02-22") vira só dígitos na ordem da tela.
const pastedISOToDigits = (raw: string, dateType: CustomFieldDateType) => {
  const match = raw.match(PASTED_ISO)
  if (!match || dateType === 'year_only') return null

  const [, year, month, day] = match
  if (dateType === 'month_year') return `${pad(month)}${year}`
  return day ? `${pad(day)}${pad(month)}${year}` : null
}

// Junta os dígitos respeitando a barra que o usuário digitou: "1/2/2018" vira
// "01022018", e não "122018".
const typedToDigits = (raw: string, dateType: CustomFieldDateType) => {
  const parts = raw.split(/\D+/)
  return parts.reduce((digits, part, index) => {
    const followedBySeparator = index < parts.length - 1
    const startsField = PAD_BOUNDARIES[dateType].includes(digits.length)
    return (
      digits +
      (followedBySeparator && startsField && part.length === 1
        ? pad(part)
        : part)
    )
  }, '')
}

// Aceita o que o usuário digitar (com ou sem barras) e devolve com as barras
// no lugar: "22022018" -> "22/02/2018", "1/2/2018" -> "01/02/2018".
export const maskDateInput = (raw: string, dateType: CustomFieldDateType) => {
  const digits = (
    pastedISOToDigits(raw, dateType) ?? typedToDigits(raw, dateType)
  ).slice(0, MAX_DIGITS[dateType])

  if (dateType === 'year_only') return digits
  if (dateType === 'month_year') {
    return digits.replace(/^(\d{2})(\d)/, '$1/$2')
  }
  return digits
    .replace(/^(\d{2})(\d)/, '$1/$2')
    .replace(/^(\d{2}\/\d{2})(\d)/, '$1/$2')
}

// "22/02/2018" -> "2018-02-22". Devolve null se a data estiver incompleta ou
// não existir (ex.: 31/02/2018).
export const displayDateToISO = (
  text: string,
  dateType: CustomFieldDateType
): string | null => {
  const digits = text.replace(/\D/g, '')
  if (digits.length !== MAX_DIGITS[dateType]) return null

  if (dateType === 'year_only') {
    return isValidParts(Number(digits)) ? digits : null
  }

  if (dateType === 'month_year') {
    const month = digits.slice(0, 2)
    const year = digits.slice(2)
    return isValidParts(Number(year), Number(month)) ? `${year}-${month}` : null
  }

  const day = digits.slice(0, 2)
  const month = digits.slice(2, 4)
  const year = digits.slice(4)
  return isValidParts(Number(year), Number(month), Number(day))
    ? `${year}-${month}-${day}`
    : null
}

export const isValidISODate = (value: string, dateType: CustomFieldDateType) =>
  isoDateToLocalDate(value, dateType) !== null

// Monta a data no fuso local. `new Date('2018-02-22')` é interpretado como UTC
// e, no Brasil, cai no dia anterior.
export const isoDateToLocalDate = (
  value: unknown,
  dateType: CustomFieldDateType
): Date | null => {
  if (!value) return null
  const match = String(value).match(ISO_PATTERN[dateType])
  if (!match) return null

  const [, year, month = '01', day = '01'] = match
  if (!isValidParts(Number(year), Number(month), Number(day))) return null
  return new Date(Number(year), Number(month) - 1, Number(day))
}

export const localDateToISO = (
  date: Date | null,
  dateType: CustomFieldDateType
): string => {
  if (!date || isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  if (dateType === 'year_only') return `${year}`
  if (dateType === 'month_year') return `${year}-${month}`
  return `${year}-${month}-${day}`
}

// "2018-02-22" -> "22/02/2018". Valor fora do formato ISO volta como veio,
// pra não sumir com o que o usuário digitou.
export const isoDateToDisplay = (
  value: unknown,
  dateType: CustomFieldDateType
): string => {
  if (!value) return ''
  const str = String(value)
  const match = str.match(ISO_PATTERN[dateType])
  if (!match) return str

  const [, year, month, day] = match
  if (dateType === 'year_only') return year
  if (dateType === 'month_year') return `${month}/${year}`
  return `${day}/${month}/${year}`
}
